import { useFormContext } from 'react-hook-form'
import { DEFAULT_ADDITIONAL_OBJECTS_LIMIT, IntegrationServiceId } from '../../../../../../../common/integrations'
import { IntegrationSettings } from '../../../schema'
import { useMemo, useState } from 'react'
import Row from 'react-bootstrap/esm/Row'
import { Accordion, Col, Dropdown, Form } from 'react-bootstrap'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { DetailedCRMAccountIntegrationMapping, MappingType } from '../../../../../../../models/Account'
import { AdditionalObjectsBody } from './AdditionalObjectsBody'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetSupportedObjectsQuery } from '../../../../../../../store/integrations/integrations'
import { useGetIntegrationAdminSettings } from '../../../hooks'

interface AdditionalObjectRowProps {
  serviceId: IntegrationServiceId
  index: number
}

export const AdditionalObjectRow: React.FC<AdditionalObjectRowProps> = ({ serviceId, index }) => {
  const { setValue, watch } = useFormContext<IntegrationSettings>()

  const [activeKey, setActiveKey] = useState<string | null>(null)

  const accountId = useAppSelector(selectAccountId)
  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )
  const additionalObjects = watch('mappings.additionalObjects') as DetailedCRMAccountIntegrationMapping[]

  const objectName = useMemo(() => additionalObjects[index]?.objectName, [additionalObjects, index])

  const isDisabled: boolean = useMemo(
    () => !!(additionalObjects[index]?.additionalInformation?.isSessionObject || additionalObjects[index]?.additionalInformation?.isTaskObject),
    [additionalObjects, index],
  )

  const objectsLimit = useMemo(() => additionalObjects[index]?.limit || DEFAULT_ADDITIONAL_OBJECTS_LIMIT, [additionalObjects, index])

  const options = useMemo(() => {
    return supportedObjects?.map((obj) => ({ id: obj.name, label: `${obj.label} (${obj.name})` })) || []
  }, [supportedObjects])

  const initialSelectedOption: SelectOption | null = useMemo(() => options?.find((opt) => opt.id === objectName) || null, [options, objectName])

  const handleCustomOptionSelect = (option: { id: string; label: string }) => {
    setValue(
      `mappings.additionalObjects.${index}`,
      { objectName: option.id, mappingType: MappingType.CUSTOM, limit: objectsLimit },
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const handleDelete = () => {
    setActiveKey(null)

    const currentList = additionalObjects || []
    const updatedList = currentList.filter((_, i) => i !== index)
    setValue('mappings.additionalObjects', updatedList, { shouldDirty: true, shouldValidate: true })
  }

  const handleDuplicate = () => {
    const currentList = additionalObjects || []

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isSessionObject, isTaskObject, ...rest } = currentList[index]?.additionalInformation ?? {}

    const itemToClone: DetailedCRMAccountIntegrationMapping = {
      ...currentList[index],
      additionalInformation: rest,
    }

    const updatedList = [...currentList]
    updatedList.splice(index + 1, 0, { ...itemToClone })

    setValue('mappings.additionalObjects', updatedList, { shouldDirty: true, shouldValidate: true })
  }

  const onLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    const numericValue = value === '' ? 0 : parseInt(value, 10)
    const currentList = additionalObjects || []
    const updatedObject = { ...currentList[index], limit: numericValue }
    const updatedList = [...currentList]
    updatedList[index] = updatedObject

    setValue('mappings.additionalObjects', updatedList, { shouldDirty: true, shouldValidate: true })
  }

  const handleAccordionSelect = (eventKey: string | null | undefined) => {
    if (eventKey === String(index)) {
      setActiveKey(String(index))
    } else {
      setActiveKey(null)
    }
  }

  return (
    <Row>
      <Col className='p-0'>
        <Accordion activeKey={activeKey} onSelect={(k) => handleAccordionSelect(k as string | null)}>
          <Accordion.Item eventKey={String(index)}>
            <Accordion.Header>
              <div className='flex-grow-1 me-3'>
                <div
                  className='w-50 input-group-sm'
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                  }}
                >
                  <SelectDropdown
                    options={options}
                    initialSelectedOption={initialSelectedOption}
                    placeholder='Select Object Type'
                    onOptionSelected={handleCustomOptionSelect}
                    searchable
                    loading={isLoadingSupportedObjects}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div onClick={(e) => e.stopPropagation()} className='pe-auto'>
                <Dropdown>
                  <Dropdown.Toggle
                    as='div'
                    role='button'
                    bsPrefix='p-0'
                    className='d-flex align-items-center justify-content-center rounded zocks-bg-color-gray-200 zocks-text-gray-900 me-3'
                  >
                    <i className='bi bi-three-dots-vertical fs-5' />
                  </Dropdown.Toggle>

                  <Dropdown.Menu align='end'>
                    <Dropdown.Item onClick={handleDuplicate} className='rounded'>
                      <i className='bi bi-files me-2' /> Duplicate
                    </Dropdown.Item>

                    <Dropdown.Item onClick={handleDelete} className='text-danger rounded' disabled={isDisabled}>
                      <i className='bi bi-trash me-2' /> Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div onClick={(e) => e.stopPropagation()} className='ms-2 me-3 pe-auto'>
                <Form.Control
                  type='text'
                  defaultValue={objectsLimit}
                  className='text-center px-1'
                  style={{ width: '60px' }}
                  maxLength={3}
                  onKeyDown={(e) => {
                    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault()
                    } else if (e.key === '0' && e.currentTarget.value === '') {
                      e.preventDefault()
                    }
                  }}
                  onChange={onLimitChange}
                />
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <AdditionalObjectsBody serviceId={serviceId} additionalObjectIndex={index} isDisabled={isDisabled} />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Col>
    </Row>
  )
}
